import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { SelectDropdown } from '@/components/ui/SelectDropdown/SelectDropdown';
import { AutoExpandScrollTextarea } from '@/components/ui/AutoExpandScrollTextarea/AutoExpandScrollTextarea';
import {
  CANCEL_REASON_SELECT_OPTIONS,
  inferCancelReasonPreset,
  noteForCancelPreset,
} from '@/components/SessionDialog/sessionCancelReason';
import './SessionCancelReasonField.css';

const NOTE_MAX = 300;

export interface SessionCancelReasonFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Показывать подпись у селекта */
  showLabel?: boolean;
}

export type SessionCancelReasonFieldHandle = {
  /** false, если выбрана «Другая причина» без текста (и подсвечивает поле). */
  validate: () => boolean;
};

export const SessionCancelReasonField = forwardRef<SessionCancelReasonFieldHandle, SessionCancelReasonFieldProps>(
  function SessionCancelReasonField({ value, onChange, showLabel = true }, ref) {
    const inferred = inferCancelReasonPreset(value);
    const [presetId, setPresetId] = useState<string>(inferred.presetId);
    const [custom, setCustom] = useState(inferred.custom);
    const [otherInvalid, setOtherInvalid] = useState(false);

    /**
     * Пустая строка с родителя неоднозначна: «Не указана» и «Другая причина» без текста.
     * После выбора «Другая причина» вызывается onChange('') — не сбрасываем preset, пока prev === 'other'.
     */
    useEffect(() => {
      const inf = inferCancelReasonPreset(value);
      setPresetId((prev) => {
        if (value === '' && inf.presetId === 'none' && prev === 'other') {
          return 'other';
        }
        return String(inf.presetId);
      });
      setCustom(inf.custom);
    }, [value]);

    const applyPreset = useCallback(
      (id: string) => {
        setOtherInvalid(false);
        setPresetId(id);
        if (id === 'none') {
          setCustom('');
          onChange('');
          return;
        }
        if (id === 'other') {
          setCustom('');
          onChange('');
          return;
        }
        setCustom('');
        onChange(noteForCancelPreset(id, ''));
      },
      [onChange]
    );

    const handleCustomChange = useCallback(
      (next: string) => {
        setOtherInvalid(false);
        const sliced = next.slice(0, NOTE_MAX);
        setCustom(sliced);
        if (presetId === 'other') {
          onChange(sliced);
        }
      },
      [onChange, presetId]
    );

    useImperativeHandle(
      ref,
      () => ({
        validate: () => {
          if (presetId === 'other' && !custom.trim()) {
            setOtherInvalid(true);
            return false;
          }
          setOtherInvalid(false);
          return true;
        },
      }),
      [presetId, custom]
    );

    return (
      <div className="session-cancel-reason-field">
        <SelectDropdown
          label={showLabel ? 'Причина отмены' : undefined}
          fullWidth
          value={presetId}
          onChange={applyPreset}
          options={CANCEL_REASON_SELECT_OPTIONS}
          placeholder="— Выберите —"
        />
        {presetId === 'other' ? (
          <div
            className={`form__field form__field_full session-cancel-reason-field__custom ${otherInvalid ? 'session-cancel-reason-field__custom_invalid' : ''}`}
          >
            <AutoExpandScrollTextarea
              value={custom}
              onChange={(e) => handleCustomChange(e.target.value)}
              placeholder="Укажите причину"
              maxLength={NOTE_MAX}
              maxVisibleLines={4}
              fullWidth
              characterCount={{ current: custom.length, max: NOTE_MAX }}
              autoComplete="off"
            />
          </div>
        ) : null}
      </div>
    );
  }
);

SessionCancelReasonField.displayName = 'SessionCancelReasonField';
